import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { StandardResponse } from '@core/models/generic.model';
import {
  ICardsDashboardSmartOlt,
  IDashboardGraphicPerDayDto,
  IDetailAuthorizationPerDay,
  IParsedData,
  IPonAffectedOnu,
  IPonDiagnosticOnu,
  IPonRef,
  IPonOutage,
  IInfoFilters,
  IInfoList,
  INetworkStatusSeries,
  IPonOutageSection,
  IPonSignalVariations,
  ISmartOltConfig,
  ISmartOltConfigCreate,
  ODBs,
  Onus,
  ProfileSpeed,
  SnAllowedValues,
  Zones,
} from '@modules/network-manager/pages/smart-olt/definitions/smart-olt.model';
import { DialogService } from 'primeng/dynamicdialog';
import { GlobalStateService } from 'src/app/shared/services/global-state/global-state.service';
import { DetailAuthotizationSeriesPerDayComponent } from '@modules/network-manager/pages/smart-olt/pages/dashboard/components/detail-authotization-series-per-day/detail-authotization-series-per-day.component';
import { ConfigSmartOltFormComponent } from '@modules/configs/pages/companies/components/config-smart-olt-form/config-smart-olt-form.component';
@Injectable({
  providedIn: 'root',
})
export class SmartOltService {
  constructor(
    private readonly http: HttpClient,
    public readonly dialogService: DialogService,
    private readonly globalStateService: GlobalStateService,
  ) {}

  getAllOnus(): Promise<StandardResponse<Onus[]>> {
    return firstValueFrom(
      this.http.get<StandardResponse<Onus[]>>(`${environment.apiUrl}/smart-olt/all-onus-type`),
    );
  }

  getAllZones(): Promise<StandardResponse<Zones[]>> {
    return firstValueFrom(
      this.http.get<StandardResponse<Zones[]>>(`${environment.apiUrl}/smart-olt/zones`),
    );
  }

  getPlansSpeed(
    companyOwnerId?: number,
    direction?: 'download' | 'upload',
  ): Promise<StandardResponse<ProfileSpeed[]>> {
    let params = new HttpParams();
    if (companyOwnerId !== undefined) {
      params = params.set('companyOwnerId', companyOwnerId.toString());
    }
    if (direction) {
      params = params.set('direction', direction);
    }
    return firstValueFrom(
      this.http.get<StandardResponse<ProfileSpeed[]>>(
        `${environment.apiUrl}/smart-olt/plans-speed`,
        { params },
      ),
    );
  }

  getAllOdbs(): Promise<StandardResponse<ODBs[]>> {
    return firstValueFrom(
      this.http.get<StandardResponse<ODBs[]>>(`${environment.apiUrl}/smart-olt/all-odbs`),
    );
  }

  getAllOnusComplete(): Promise<StandardResponse<ODBs[]>> {
    return firstValueFrom(
      this.http.get<StandardResponse<ODBs[]>>(`${environment.apiUrl}/smart-olt/get-all-onus`),
    );
  }

  /**
   * getCardsDashboardSmartOlt
   */
  public getCardsDashboardSmartOlt(oltId?: number | null) {
    return firstValueFrom(
      this.http.get<StandardResponse<ICardsDashboardSmartOlt>>(
        `${environment.apiUrl}/smart-olt/get-card-info-dashboard`,
        { params: this.oltParams(oltId) },
      ),
    );
  }

  /**
   * getGraphAuthorizationPerDayOnu
   */
  public getGraphAuthorizationPerDayOnu(oltId?: number | null) {
    return firstValueFrom(
      this.http.get<StandardResponse<IDashboardGraphicPerDayDto[]>>(
        `${environment.apiUrl}/smart-olt/get-graphic-per-day`,
        { params: this.oltParams(oltId) },
      ),
    );
  }

  public getDetailAuthorizationPerDay(
    date: string,
    type: 'gpon' | 'epon',
  ): Promise<StandardResponse<IDetailAuthorizationPerDay>> {
    const params = new HttpParams().set('date', date).set('type', type);
    return firstValueFrom(
      this.http.get<StandardResponse<IDetailAuthorizationPerDay>>(
        `${environment.apiUrl}/smart-olt/get-detail-graphic-onu-per-day`,
        { params },
      ),
    );
  }

  /**
   * openModalDetailAuthorizationPerDay
   */
  public async openModalDetailAuthorizationPerDay(date: string, type: 'gpon' | 'epon') {
    this.globalStateService.setShowLoading(true);
    try {
      const response = await this.getDetailAuthorizationPerDay(date, type);
      this.dialogService.open(DetailAuthotizationSeriesPerDayComponent, {
        header: `ONU's ${type.toUpperCase()} Autorizadas el día ${date}`,
        data: response.data,
        maximizable: true,
        width: '90%',
        height: '100vh',
        breakpoints: { '960px': '80vw', '640px': '90vw' },
      });
    } catch (error) {
    } finally {
      this.globalStateService.setShowLoading(false);
    }
  }

  /**
   * getPonOutages
   */
  public getPonOutages(oltId?: number | null) {
    return firstValueFrom(
      this.http.get<
        StandardResponse<{
          pons: Array<IPonOutage>;
          sections: Array<IPonOutageSection>;
          signalVariations: IPonSignalVariations;
          total: number;
        }>
      >(`${environment.apiUrl}/smart-olt/get-pon-outage`, { params: this.oltParams(oltId) }),
    );
  }

  /**
   * getGraphicDashboard
   */
  public getGraphicDashboard(type: SnAllowedValues) {
    return firstValueFrom(
      this.http.get<any>(`${environment.apiUrl}/smart-olt/get-graphic-dashboard/${type}`, {
        responseType: 'blob' as 'json',
      }),
    );
  }

  /**
   * getOnusStatusesSeries
   */
  public getOnusStatusesSeries(period: string, oltId?: number | null) {
    return firstValueFrom(
      this.http.get<StandardResponse<Array<INetworkStatusSeries>>>(
        `${environment.apiUrl}/smart-olt/get-onus-statuses-series/${period}`,
        { params: this.oltParams(oltId) },
      ),
    );
  }

  /**
   * getInfoList
   */
  public getInfoList(filters: IInfoFilters) {
    const params: Record<string, string> = { page: String(filters.page) };
    (['olt_id', 'user_id', 'free_text', 'from', 'to'] as const).forEach((key) => {
      if (filters[key]) params[key] = filters[key];
    });
    return firstValueFrom(
      this.http.get<StandardResponse<IInfoList>>(`${environment.apiUrl}/smart-olt/get-info-list`, {
        params,
      }),
    );
  }

  /**
   * getInfoUsers
   */
  public getInfoUsers() {
    return firstValueFrom(
      this.http.get<StandardResponse<Array<{ value: string; label: string }>>>(
        `${environment.apiUrl}/smart-olt/get-info-users`,
      ),
    );
  }

  /**
   * getDashboardData
   */
  public getDashboardData(oltId?: number | null) {
    return firstValueFrom(
      this.http.get<StandardResponse<IParsedData>>(
        `${environment.apiUrl}/smart-olt/get-dashboard-data`,
        { params: this.oltParams(oltId) },
      ),
    );
  }

  /**
   * getPonAffectedOnus: ONUs afectadas por la caída de una PON
   */
  public getPonAffectedOnus(pon: IPonRef) {
    return firstValueFrom(
      this.http.get<StandardResponse<Array<IPonAffectedOnu>>>(
        `${environment.apiUrl}/smart-olt/get-pon-affected-onus`,
        { params: this.ponParams(pon) },
      ),
    );
  }

  /**
   * getPonOnus: todas las ONUs de una PON (diagnóstico), por páginas
   */
  public getPonOnus(pon: IPonRef, page = 1) {
    return firstValueFrom(
      this.http.get<
        StandardResponse<{ rows: Array<IPonDiagnosticOnu>; page: number; total_pages: number }>
      >(`${environment.apiUrl}/smart-olt/get-pon-onus`, {
        params: { ...this.ponParams(pon), page: String(page) },
      }),
    );
  }

  private ponParams(pon: IPonRef): Record<string, string> {
    return { oltId: String(pon.olt_id), board: String(pon.board), port: String(pon.port) };
  }

  /** Dashboard de una sola OLT (como ?olt_id= de SmartOLT); sin OLT, el de todas. */
  private oltParams(oltId?: number | null): Record<string, string> {
    return oltId ? { oltId: String(oltId) } : {};
  }

  /**
   * getConfigByCompanyOwner
   */
  public getConfigByCompanyOwner(companyOwner: number) {
    return firstValueFrom(
      this.http.get<StandardResponse<ISmartOltConfig>>(
        `${environment.apiUrl}/smart-olt-config/${companyOwner}/company-owner`,
      ),
    );
  }

  /**
   * openConfigByCompanyOwner
   */
  public async openConfigByCompanyOwner(companyOwner: number) {
    try {
      const response = await this.getConfigByCompanyOwner(companyOwner);
      const config: Partial<ISmartOltConfig> = response.data
        ? response.data
        : ({ companyOwnerId: companyOwner } as Partial<ISmartOltConfig>);
      const isCreate = !response.data;
      this.dialogService.open(ConfigSmartOltFormComponent, {
        header: `${isCreate ? 'Crear' : 'Actualizar'} configuración Smart OLT`,
        data: { id: (config as any).id, config: config, create: isCreate },
        maximizable: true,
        width: '60%',
        breakpoints: { '960px': '80vw', '640px': '90vw' },
      });
    } catch (error) {}
  }

  /**
   * updateConfigSmartOlt
   */
  public updateConfigSmartOlt(idConfig: number, data: Partial<ISmartOltConfigCreate>) {
    return firstValueFrom(
      this.http.put<StandardResponse<void>>(
        `${environment.apiUrl}/smart-olt-config/${idConfig}`,
        data,
      ),
    );
  }

  /**
   * createConfigSmartOlt
   */
  public createConfigSmartOlt(data: ISmartOltConfigCreate) {
    return firstValueFrom(
      this.http.post<StandardResponse<void>>(`${environment.apiUrl}/smart-olt-config`, data),
    );
  }
}
